import React, { useState } from 'react'
import { Cell } from 'react-sticky-table'

import Icon from '../../../Icons/Icon'
import styles from '../../SpreadsheetTable/ColumnHeader/_column-header.module.scss'
import {
  FormFooter,
  SideDrawer,
  Tag,
  Tooltip,
  type TooltipProps,
} from '../../../../module'
import { c } from '../../../../translations/LibraryTranslationService'

type ColumnHeaderBase = {
  /** Header props */
  header: {
    /** Content of the header */
    text: string
    /** Tooltip content */
    tooltipContent?: TooltipProps['tooltipContent']
    /** Background color  */
    bgColor: 'blue' | 'red' | 'light-gray' | 'yellow' | 'light-purple' | 'white'
  }
  subHeader?: {
    /** Content of the sub header */
    text: string
    /** Tooltip content */
    tooltipContent?: TooltipProps['tooltipContent']
    /** Sub header color */
    color: 'red' | 'purple' | 'yellow'
  }
  /** Dynamic styles passed in from the parent component */
  containerClassName?: string
  /** Needed to determine the width of the column */
  columnWidth: number
  onHeaderCellClick: () => void
  headerCancelCallout?: () => void
  disabled?: boolean
  headerText: string
  headerChildren?: React.ReactNode
}

type WithCellCount = ColumnHeaderBase & {
  errorCount: number
  showCount: boolean
}

type WithoutCellCount = ColumnHeaderBase & {
  errorCount?: never
  showCount?: never
}

export type ColumnHeaderType = WithCellCount | WithoutCellCount

const ColumnHeader = ({
  errorCount,
  header,
  showCount,
  subHeader,
  containerClassName = '',
  columnWidth,
  onHeaderCellClick,
  disabled,
  headerText,
  headerChildren,
  headerCancelCallout,
}: ColumnHeaderType) => {
  const [isOpen, setIsOpen] = useState(false)
  const applyCallout = () => {
    onHeaderCellClick?.()
  }

  return (
    <>
      {headerChildren ? (
        <SideDrawer
          isOpen={isOpen}
          closeCallout={() => {
            setIsOpen(false)
            headerCancelCallout?.()
          }}
          headerContent={headerText}
          footerContent={({ close }) => (
            <FormFooter
              cancelButtonProps={{
                onClick: () => {
                  close()
                  headerCancelCallout?.()
                },
                children: c('close'),
              }}
              saveButtonProps={{
                onClick: () => {
                  applyCallout()
                  close()
                },
                disabled: disabled,
                children: c('save'),
              }}
            />
          )}
        >
          {headerChildren}
        </SideDrawer>
      ) : null}
      <Cell
        className={`${containerClassName} ${styles.cellContainer} ${styles[header.bgColor]}`}
        style={{
          // NOTE: This will make every column stretch at an equal width to fill the given space when there are few columns. There is not currently a maxWidth so we will need to look into that.
          width: columnWidth,
        }}
        onClick={() => {
          applyCallout()
          setIsOpen(true)
        }}
      >
        <div className={styles.columnHeaderContainer}>
          <div>
            <div className={`${styles.heading} ${styles[header.bgColor]}`}>
              {header.text}
              {header.tooltipContent ? (
                <Tooltip tooltipContent={header.tooltipContent}>
                  <Icon icon='info' iconSize='12px' />
                </Tooltip>
              ) : null}
            </div>
            {subHeader?.text && (
              <div
                className={`${styles.subHeader} ${styles[subHeader?.color ?? 'purple']}`}
              >
                <span>{subHeader?.text}</span>
                {subHeader?.tooltipContent ? (
                  <Tooltip tooltipContent={subHeader?.tooltipContent}>
                    <Icon
                      icon='info'
                      iconSize='12px'
                      color={
                        subHeader?.color === 'red'
                          ? 'dark-red'
                          : subHeader?.color === 'yellow'
                            ? 'dark-yellow'
                            : 'purple'
                      }
                    />
                  </Tooltip>
                ) : null}
              </div>
            )}
          </div>
          {showCount ? (
            <div>
              {errorCount && errorCount > 0 ? (
                <Tag color='red'>{errorCount}</Tag>
              ) : (
                <Tag color='green'>
                  <Icon
                    icon='checkNoCircle'
                    iconSize='12px'
                    color='dark-green'
                  />
                </Tag>
              )}
            </div>
          ) : null}
        </div>
      </Cell>
    </>
  )
}
export default ColumnHeader
